import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Building,
  DollarSign,
  TrendingUp,
  AlertCircle,
  FileText,
  BarChart3,
  Calendar,
  Activity,
  MapPin,
  Clock,
  Plus,
  Eye,
  ArrowRight
} from 'lucide-react';
import { useWorks, useDashboardData, useWorksStats } from '../../contexts/WorksContext';
import { worksApiService } from '../../services/workApi.service';
import { WorkStage } from '../../types/work.types';

// ==========================================
// STAT CARD COMPONENT
// ==========================================

interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: number;
  color?: 'blue' | 'green' | 'orange' | 'red' | 'purple';
  loading?: boolean;
  action?: {
    label: string;
    href: string;
  };
}

const StatCard: React.FC<StatCardProps> = ({ 
  icon: Icon, 
  title, 
  value, 
  subtitle, 
  trend, 
  color = 'blue',
  loading = false,
  action
}) => {
  const colorClasses = {
    blue: 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 text-blue-900',
    green: 'bg-gradient-to-br from-green-50 to-green-100 border-green-200 text-green-900',
    orange: 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 text-orange-900',
    red: 'bg-gradient-to-br from-red-50 to-red-100 border-red-200 text-red-900',
    purple: 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 text-purple-900'
  };

  if (loading) {
    return (
      <div className="p-6 rounded-xl border-2 bg-gray-50 border-gray-200 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="w-8 h-8 bg-gray-300 rounded"></div>
          <div className="w-12 h-4 bg-gray-300 rounded"></div>
        </div>
        <div className="space-y-2">
          <div className="w-24 h-8 bg-gray-300 rounded"></div>
          <div className="w-32 h-4 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 rounded-xl border-2 ${colorClasses[color]} shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 relative group`}>
      <div className="flex items-center justify-between mb-4">
        <Icon className="w-8 h-8 opacity-80" />
        {trend !== undefined && (
          <div className={`flex items-center text-sm font-medium ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-600'}`}>
            <TrendingUp className={`w-4 h-4 mr-1 ${trend < 0 ? 'rotate-180' : ''}`} />
            {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>
      <div className="space-y-1">
        <p className="text-2xl font-bold">{typeof value === 'number' ? value.toLocaleString('en-IN') : value}</p>
        <p className="text-sm font-medium opacity-80">{title}</p>
        {subtitle && <p className="text-xs opacity-60">{subtitle}</p>}
      </div>
      
      {action && (
        <Link
          to={action.href}
          className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 bg-black bg-opacity-5 flex items-center justify-center transition-all duration-200"
        >
          <span className="text-sm font-medium opacity-80 flex items-center">
            {action.label}
            <ArrowRight className="w-4 h-4 ml-1" />
          </span>
        </Link>
      )}
    </div>
  );
};

// ==========================================
// QUICK STATS COMPONENT
// ==========================================

const QuickStats: React.FC = () => {
  const { quickStats } = useWorksStats();
  
  if (!quickStats) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <StatCard
            key={i}
            icon={Building}
            title="Loading..."
            value="--"
            loading={true}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatCard
        icon={Building}
        title="Total Works"
        value={quickStats.totalWorks}
        subtitle="Active projects"
        color="blue"
        action={{ label: "View all works", href: "/works/list" }}
      />
      <StatCard
        icon={DollarSign}
        title="Total Sanctioned"
        value={worksApiService.formatCurrency(quickStats.totalSanctioned)}
        subtitle="Approved budget"
        trend={quickStats.utilizationPercentage - 75}
        color="green"
        action={{ label: "Financial details", href: "/works/analytics" }}
      />
      <StatCard
        icon={TrendingUp}
        title="Utilization Rate"
        value={`${quickStats.utilizationPercentage.toFixed(1)}%`}
        subtitle={worksApiService.formatCurrency(quickStats.totalSpent) + " spent"}
        color="orange"
        action={{ label: "View progress", href: "/works/analytics" }}
      />
      <StatCard
        icon={AlertCircle}
        title="Pending Amount"
        value={worksApiService.formatCurrency(quickStats.pendingBillsAmount)}
        subtitle="Bills & outstanding work"
        color="red"
        action={{ label: "View reports", href: "/works/reports" }}
      />
    </div>
  );
};

// ==========================================
// WORKS BY STAGE COMPONENT
// ==========================================

const WorksByStage: React.FC = () => {
  const { worksByStage } = useWorksStats();
  
  const stageConfig = {
    [WorkStage.NOT_STARTED]: { color: 'from-gray-400 to-gray-600', textColor: 'text-gray-700' },
    [WorkStage.IN_PROGRESS]: { color: 'from-blue-400 to-blue-600', textColor: 'text-blue-700' },
    [WorkStage.COMPLETED]: { color: 'from-green-400 to-green-600', textColor: 'text-green-700' },
    [WorkStage.ON_HOLD]: { color: 'from-red-400 to-red-600', textColor: 'text-red-700' },
    [WorkStage.PLANNING]: { color: 'from-orange-400 to-orange-600', textColor: 'text-orange-700' },

  };

  const totalWorks = Object.values(worksByStage).reduce((sum, count) => sum + count, 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2 text-purple-600" />
          Works by Stage
        </h3>
        <Link
          to="/works/analytics"
          className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
        >
          View analytics
          <ArrowRight className="w-4 h-4 ml-1" />
        </Link>
      </div>
      
      <div className="space-y-4">
        {Object.entries(WorkStage).map(([key, stage]) => {
          const count = worksByStage[stage] || 0;
          const percentage = totalWorks > 0 ? (count / totalWorks) * 100 : 0;
          const config = stageConfig[stage];
          
          return (
            <div key={key} className="space-y-2">
              <div className="flex justify-between items-center">
                <span className={`text-sm font-medium ${config.textColor}`}>{stage}</span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">{count} works</span>
                  <span className="text-xs text-gray-500">({percentage.toFixed(1)}%)</span>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full bg-gradient-to-r ${config.color} transition-all duration-500`}
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==========================================
// RECENT ACTIVITIES COMPONENT
// ==========================================

const RecentActivities: React.FC = () => {
  const activities = [
    {
      id: 1,
      type: 'work_created',
      title: 'New work added',
      description: 'SNO 1045 - Village Road Construction',
      time: '2 hours ago',
      icon: Plus,
      color: 'text-green-600'
    },
    {
      id: 2,
      type: 'stage_updated',
      title: 'Work progress updated',
      description: 'SNO 1032 moved to "In Progress"',
      time: '4 hours ago',
      icon: Activity,
      color: 'text-blue-600'
    },
    {
      id: 3,
      type: 'work_completed',
      title: 'Work completed',
      description: 'SNO 1018 - Bridge Construction',
      time: '1 day ago',
      icon: TrendingUp,
      color: 'text-purple-600'
    },
    {
      id: 4,
      type: 'delay_alert',
      title: 'Delay alert',
      description: 'SNO 1025 is past deadline',
      time: '1 day ago',
      icon: AlertCircle,
      color: 'text-red-600'
    }
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Activity className="w-5 h-5 mr-2 text-indigo-600" />
          Recent Activities
        </h3>
        <Link
          to="/works/list"
          className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
        >
          View all
          <ArrowRight className="w-4 h-4 ml-1" />
        </Link>
      </div>
      
      <div className="space-y-4">
        {activities.map((activity) => {
          const Icon = activity.icon;
          return (
            <div key={activity.id} className="flex items-start space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
              <div className={`p-2 rounded-full bg-gray-100 ${activity.color}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">{activity.title}</p>
                <p className="text-sm text-gray-600 truncate">{activity.description}</p>
                <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==========================================
// MAIN WORKS DASHBOARD COMPONENT
// ==========================================

const WorksDashboard: React.FC = () => {
  const { fetchDashboardData, fetchProgressData } = useWorks();
  const { data: dashboardData, loading, error } = useDashboardData();
  const { delayedWorks } = useWorksStats();

  useEffect(() => {
    fetchDashboardData();
    fetchProgressData();
  }, [fetchDashboardData, fetchProgressData]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Dashboard Error</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => fetchDashboardData()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Works Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Overview of construction works and financial progress
          </p>
        </div>
        <div className="flex space-x-3">
          <Link
            to="/works/create"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Work</span>
          </Link>
          <Link
            to="/works/analytics"
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
          >
            <BarChart3 className="w-4 h-4" />
            <span>Analytics</span>
          </Link>
        </div>
      </div>

      {/* Quick Stats */}
      <QuickStats />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Works by Stage - Takes 2 columns */}
        <div className="lg:col-span-2">
          <WorksByStage />
        </div>

        {/* Recent Activities - Takes 1 column */}
        <div>
          <RecentActivities />
        </div>
      </div>

      {/* Delayed Works Alert */}
      {delayedWorks && delayedWorks.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-red-900 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              Works Requiring Attention ({delayedWorks.length})
            </h3>
            <Link
              to="/works/list?stage=delayed"
              className="text-sm text-red-600 hover:text-red-800 flex items-center"
            >
              View all delayed works
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {delayedWorks.slice(0, 6).map((work) => (
              <div key={work.work_id} className="bg-white rounded-lg border border-red-200 p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">SNO: {work.sno}</p>
                    <p className="text-sm text-gray-600 truncate" title={work.work_name}>
                      {work.work_name}
                    </p>
                  </div>
                  <Link
                    to={`/works/view/${work.work_id}`}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                  </Link>
                </div>
                <div className="space-y-1 text-xs text-gray-600">
                  <div className="flex items-center">
                    <MapPin className="w-3 h-3 mr-1" />
                    {work.district?.name}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    Due: {worksApiService.formatDate(work.completion_date_set!)}
                  </div>
                  <div className="flex items-center">
                    <DollarSign className="w-3 h-3 mr-1" />
                    {worksApiService.formatCurrency(work.estimate_cost)}
                  </div>
                </div>
                <div className="mt-3 text-xs text-red-600 font-medium">
                  {Math.floor((new Date().getTime() - new Date(work.completion_date_set!).getTime()) / (1000 * 60 * 60 * 24))} days overdue
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Financial Overview */}
      {dashboardData?.overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <DollarSign className="w-6 h-6 text-green-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Financial Health</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Utilization Rate</span>
                <span className="font-semibold text-green-600">
                  {dashboardData.overview.utilization_percentage.toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Avg Work Cost</span>
                <span className="font-semibold">
                  {worksApiService.formatCurrency(dashboardData.overview.avg_work_cost)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pending Bills</span>
                <span className="font-semibold text-orange-600">
                  {dashboardData.overview.pending_bills_percentage.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(dashboardData.overview.utilization_percentage, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Clock className="w-6 h-6 text-blue-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Project Timeline</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Works</span>
                <span className="font-semibold text-blue-600">
                  {dashboardData.overview.total_works}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Avg Cost</span>
                <span className="font-semibold text-green-600">
                  {worksApiService.formatCurrency(dashboardData.overview.avg_work_cost)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pending Bills</span>
                <span className="font-semibold text-orange-600">
                  {worksApiService.formatCurrency(dashboardData.overview.pending_with_pao + dashboardData.overview.work_done_not_billed)}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Activity className="w-6 h-6 text-purple-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
            </div>
            <div className="space-y-3">
              <Link
                to="/works/create"
                className="flex items-center p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors group"
              >
                <Plus className="w-5 h-5 text-blue-600 mr-3" />
                <div className="flex-1">
                  <p className="font-medium text-blue-900">Add New Work</p>
                  <p className="text-xs text-blue-700">Create a new construction project</p>
                </div>
                <ArrowRight className="w-4 h-4 text-blue-600 group-hover:translate-x-1 transition-transform" />
              </Link>
              
              <Link
                to="/works/reports"
                className="flex items-center p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors group"
              >
                <FileText className="w-5 h-5 text-green-600 mr-3" />
                <div className="flex-1">
                  <p className="font-medium text-green-900">Generate Report</p>
                  <p className="text-xs text-green-700">Download progress reports</p>
                </div>
                <ArrowRight className="w-4 h-4 text-green-600 group-hover:translate-x-1 transition-transform" />
              </Link>
              
              <Link
                to="/works/analytics"
                className="flex items-center p-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors group"
              >
                <BarChart3 className="w-5 h-5 text-purple-600 mr-3" />
                <div className="flex-1">
                  <p className="font-medium text-purple-900">View Analytics</p>
                  <p className="text-xs text-purple-700">Performance insights</p>
                </div>
                <ArrowRight className="w-4 h-4 text-purple-600 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Grant Type Analysis */}
      {dashboardData?.grantTypeAnalysis && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-indigo-600" />
              Grant Type Analysis
            </h3>
            <Link
              to="/works/analytics"
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
            >
              View detailed analysis
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {dashboardData.grantTypeAnalysis.slice(0, 4).map((grant, index) => (
              <div key={`${grant.oas_grant_level}-${grant.grant_type}`} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{grant.grant_type}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    grant.oas_grant_level === 'Central' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {grant.oas_grant_level}
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Works</span>
                    <span className="font-medium">{grant.work_count}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Sanctioned</span>
                    <span className="font-medium">{worksApiService.formatCurrency(grant.total_sanctioned)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Spent</span>
                    <span className="font-medium">{worksApiService.formatCurrency(grant.total_spent)}</span>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${
                        index % 4 === 0 ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                        index % 4 === 1 ? 'bg-gradient-to-r from-green-400 to-green-600' :
                        index % 4 === 2 ? 'bg-gradient-to-r from-purple-400 to-purple-600' :
                        'bg-gradient-to-r from-orange-400 to-orange-600'
                      }`}
                      style={{ 
                        width: `${grant.total_sanctioned > 0 ? (grant.total_spent / grant.total_sanctioned) * 100 : 0}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly Trends */}
      {dashboardData?.trendAnalysis && dashboardData.trendAnalysis.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-orange-600" />
              Monthly Performance Trends
            </h3>
            <div className="text-sm text-gray-500">
              Last 12 months performance
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="flex space-x-4 pb-4" style={{ minWidth: '800px' }}>
              {dashboardData.trendAnalysis.slice(0, 6).map((trend, index) => (
                <div key={trend.month} className="flex-shrink-0 bg-gray-50 rounded-lg p-4 min-w-[180px] hover:bg-gray-100 transition-colors">
                  <div className="text-center mb-3">
                    <h4 className="font-medium text-gray-900">{trend.month}</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600">{trend.works_sanctioned}</div>
                      <div className="text-xs text-gray-600">works sanctioned</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-semibold text-green-600">
                        {worksApiService.formatCurrency(trend.amount_sanctioned)}
                      </div>
                      <div className="text-xs text-gray-600">sanctioned</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-semibold text-orange-600">
                        {worksApiService.formatCurrency(trend.amount_spent)}
                      </div>
                      <div className="text-xs text-gray-600">spent</div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="w-full bg-gray-200 rounded-full h-1">
                      <div
                        className="bg-gradient-to-r from-blue-400 to-green-400 h-1 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${trend.amount_sanctioned > 0 ? (trend.amount_spent / trend.amount_sanctioned) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorksDashboard;